import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";

import { FirebaseAuth } from "../../../../../components/FirebaseAuth/firebase";
import InvoiceList, { normalizeInvoice } from "../../InvoiceList";
import PaymentHistory from "../../PaymentHistory";

const formatCurrency = (amountInCents = 0, currency = "usd") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: String(currency || "usd").toUpperCase(),
  }).format(Number(amountInCents || 0) / 100);

const isPaidInvoice = (invoice = {}) => String(invoice.status || "").toLowerCase() === "paid";

const BillingMetricCard = ({ color = "primary.main", icon, label, loading, value, helper }) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, minHeight: 126 }}>
    <Stack spacing={1.25}>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Avatar variant="rounded" sx={{ bgcolor: "rgba(25, 118, 210, 0.08)", color, height: 36, width: 36 }}>
          {icon}
        </Avatar>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 850, letterSpacing: 0.45, textTransform: "uppercase" }}>
          {label}
        </Typography>
      </Stack>
      {loading ? (
        <Stack spacing={0.75}>
          <Skeleton variant="text" width="52%" height={32} />
          <Skeleton variant="text" width="74%" />
        </Stack>
      ) : (
        <>
          <Typography variant="h5" sx={{ fontWeight: 850 }}>
            {value}
          </Typography>
          {!!helper && (
            <Typography variant="body2" color="text.secondary">
              {helper}
            </Typography>
          )}
        </>
      )}
    </Stack>
  </Paper>
);

const BillingSection = ({ accountId }) => {
  const [activeTab, setActiveTab] = useState("invoices");
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(true);

  useEffect(() => {
    let mounted = true;
    const userId = FirebaseAuth.auth().currentUser?.uid;

    const fetchInvoices = async () => {
      if (!userId) {
        if (mounted) setLoadingInvoices(false);
        return;
      }

      setLoadingInvoices(true);
      try {
        const snapshot = await FirebaseAuth.firestore()
          .collection("users")
          .doc(userId)
          .collection("invoices")
          .orderBy("month", "desc")
          .get();
        const nextInvoices = snapshot.docs.map((doc) => normalizeInvoice(doc.id, doc.data() || {}));
        if (mounted) setInvoices(nextInvoices);
      } finally {
        if (mounted) setLoadingInvoices(false);
      }
    };

    fetchInvoices();
    return () => {
      mounted = false;
    };
  }, []);

  const metrics = useMemo(() => {
    const paidInvoices = invoices.filter(isPaidInvoice);
    const openInvoices = invoices.filter((invoice) => !isPaidInvoice(invoice));
    const currency = invoices[0]?.currency || "usd";
    const totalInvoiced = invoices.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const openAmount = openInvoices.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const paidAmount = paidInvoices.reduce((sum, row) => sum + Number(row.amount || 0), 0);

    return {
      currency,
      openAmount,
      openCount: openInvoices.length,
      paidAmount,
      paidCount: paidInvoices.length,
      totalCount: invoices.length,
      totalInvoiced,
    };
  }, [invoices]);

  return (
    <Stack spacing={2} sx={{ textAlign: "left" }}>
      <Paper variant="outlined" sx={{ borderColor: "#dadce0", borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ p: 0 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            sx={{ p: 2.5 }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 850, lineHeight: 1.2 }}>
                  Billing
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Review contractor invoices and payment history.
                </Typography>
              </Box>
            </Stack>
            {loadingInvoices ? (
              <Skeleton variant="rounded" width={148} height={32} />
            ) : (
              <Chip
                label={`${metrics.totalCount} invoice${metrics.totalCount === 1 ? "" : "s"}`}
                variant="outlined"
                sx={{ borderRadius: 1, fontWeight: 700 }}
              />
            )}
          </Stack>
        </Box>

        <Box sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}`, p: 2, pt: 0 }}>
          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, mt: 2 }}>
            <BillingMetricCard
              color="primary.main"
              icon={<ReceiptLongOutlinedIcon fontSize="small" />}
              label="Total invoiced"
              loading={loadingInvoices}
              value={formatCurrency(metrics.totalInvoiced, metrics.currency)}
              helper={`${metrics.totalCount} invoice${metrics.totalCount === 1 ? "" : "s"} issued`}
            />
            <BillingMetricCard
              color="warning.main"
              icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />}
              label="Open"
              loading={loadingInvoices}
              value={formatCurrency(metrics.openAmount, metrics.currency)}
              helper={`${metrics.openCount} open invoice${metrics.openCount === 1 ? "" : "s"}`}
            />
            <BillingMetricCard
              color="success.main"
              icon={<PaidOutlinedIcon fontSize="small" />}
              label="Paid"
              loading={loadingInvoices}
              value={formatCurrency(metrics.paidAmount, metrics.currency)}
              helper={`${metrics.paidCount} paid invoice${metrics.paidCount === 1 ? "" : "s"}`}
            />
          </Box>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ borderColor: "#dadce0", borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ px: 2, pt: 1 }}>
          <Tabs
            value={activeTab}
            onChange={(_event, nextValue) => setActiveTab(nextValue)}
            aria-label="Contractor billing sections"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              value="invoices"
              label={`Invoices${loadingInvoices ? "" : ` (${invoices.length})`}`}
              sx={{ fontWeight: 800, textTransform: "none" }}
            />
            <Tab
              value="payments"
              label="Payment history"
              sx={{ fontWeight: 800, textTransform: "none" }}
            />
          </Tabs>
        </Box>
        <Box sx={{ bgcolor: "grey.50", borderTop: (theme) => `1px solid ${theme.palette.divider}`, p: 2 }}>
          {activeTab === "invoices" ? (
            <InvoiceList embedded loading={loadingInvoices} invoices={invoices} />
          ) : (
            <PaymentHistory embedded accountId={accountId} />
          )}
        </Box>
      </Paper>
    </Stack>
  );
};

export default BillingSection;
